import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import { imageSize, capitalizeFirstLetter } from '@common/react/utils/utils';

import { ethnicities } from '@commonTuna/react/components/UI/CaseModalInner/CaseModalInner';
import ImageWithSkeleton from '@commonTuna/react/components/UI/ImageWithSkeleton/ImageWithSkeleton';
import { Case, CaseType, CasePage } from '@commonTuna/react/objects/BaseCase';
import { Gender } from '@commonTuna/react/objects/Enums';

interface ComponentProps {
	item: Case;
	onCaseClick: (caseId: number) => void;
	isShort: boolean;
}

const serviceNode = (page, lang) => {
	const path = lang === 'En' ? page.page?.fullPath : page.page?.fullPathEs;
	const tag = (<div className="ant-tag" key={page.page.id} title={page.page[`shortName${lang}`]} style={{ fontWeight: 600 }}>
		{page.page[`shortName${lang}`]}
	</div>);
	return !path ? tag
		: (
			<NavLink
				key={page.id}
				to={path || ''}
			>
				{tag}
			</NavLink>
		);
};

const moreTagWidth = 42;

const Services: React.FC<{items: Array<CasePage>, lang, caseId }> = ({ caseId, items, lang }) => {
	const ref = React.useRef<HTMLDivElement>(null);
	const [count, setCount] = React.useState(0);
	const tagsWidth = React.useRef<any>();

	React.useEffect(() => {
		if (ref.current) {
			const resizeObserver = new ResizeObserver((entries) => {
				typeof window !== 'undefined' && window.requestAnimationFrame(() => {
					for (const entry of entries) {
						if (entry.contentBoxSize) {
							const contentBoxSize = Array.isArray(entry.contentBoxSize) ? entry.contentBoxSize[0] : entry.contentBoxSize;
							if (!tagsWidth.current) {
								tagsWidth.current = [];
								let elementChild = ref.current?.firstElementChild;
								while (elementChild) {
									tagsWidth.current.push(elementChild.getBoundingClientRect().width);
									elementChild = elementChild.nextElementSibling;
								}
							}

							const width = (contentBoxSize?.inlineSize || entry.contentRect?.width);
							if (tagsWidth.current) {
								let elementsWidth = tagsWidth.current.reduce((acc, width) => width + acc, 0) > width ? moreTagWidth : 0;
								let i;
								for (i = 0; i < tagsWidth.current.length; i++) {
									elementsWidth += tagsWidth.current[i];
									if (width < elementsWidth) break;
								}
								setCount(tagsWidth.current.length - i);
							}
						}
					}
				});
			});

			resizeObserver.observe(ref.current);

			return () => {
				ref.current && resizeObserver.unobserve(ref.current);
			};
		}
	}, []);
	const id = `case${caseId}`;

	return <div className={`page-list ${items.length ? 'page-list__long' : ''}`} ref={ref}>
		{items.slice(0, items.length - count).map((item) => serviceNode(item, lang))}
		{count > 0 ? <>
			<button data-tooltip-id={id} type="button" className="inline-select-item inline-select-item_sm">
				+
				{count}
			</button>
			<Tooltip
				place="top"
				id={id}
				clickable
				positionStrategy="fixed"
			>
				{items.slice(items.length - count).map((item) => serviceNode(item, lang))}
			</Tooltip>
		</> : null}
	</div>;
};

const BeforeAfterBreast = (props: ComponentProps) => {
	const { item, onCaseClick, isShort } = props;
	const { t, i18n } = useTranslation();

	const lang = capitalizeFirstLetter(i18n.language);
	const name = item[`name${lang}`];

	return <div className="ba-breast clearfix">
		<div
			className="ba-breast__title"
			onClick={(e) => onCaseClick(item.id)}
			title={name}
		>
			{`#${item.indexInCompany} `}
			{isShort ? `${item[`name${lang}`].slice(0, 35)}${item[`name${lang}`].length > 35 ? '...' : ''}` : item[`name${lang}`]}
		</div>
		<div className="ba-breast__photos clearfix" onClick={(e) => onCaseClick(item.id)}>
			<div className="ba-breast__photos_left">
				<div>
					<ImageWithSkeleton
						withLazy
						src={imageSize(item.before, '_main')}
						alt={`${name} before`}
						loaderContainerClassName="ba-image__loader fa fa-file-image-o"
					/>
				</div>
			</div>
			<div className="ba-breast__photos_right">
				<div>
					<ImageWithSkeleton
						withLazy
						loaderContainerClassName="ba-image__loader fa fa-file-image-o"
						src={imageSize(item.after, '_main')}
						alt={`${name} after`}
					/>
				</div>
			</div>
		</div>
		<div className="ba-breast__body">
			<div className="row mb10">
				<div className="col-xs-12">
					<label className="param-label">
						{lang === 'En' ? `Service${item.pages?.length > 1 ? 's' : ''}` : t('site.Services')}
						:
					</label>
					<Services items={item.pages} lang={lang} caseId={item.id} />
				</div>
			</div>
			{isShort ? null : <>
				<div className="row row-short entity-container">
					<div className="col-xs-6">
						<label className="param-label">
							{t('forms.Gender')}
							:
						</label>
						{t(`forms.${Gender[item.gender]}`)}
					</div>
					<div className="col-xs-6" style={{ whiteSpace: 'nowrap' }}>
						<label className="param-label">
							{t('forms.Ethnicity')}
							:
						</label>
						{item.ethnicity ? ethnicities[item.ethnicity[`name${lang}`].toLowerCase()] || item.ethnicity[`name${lang}`] : 'N/A'}
					</div>
				</div>
				<div className="row row-short">
					<div className="col-xs-4 ellipsis">
						<label className="param-label">
							{t('forms.Age')}
							:
						</label>
						{item.age || 'N/A'}
&nbsp;
						<span className="mobile-hidden">{item.age ? lang === 'En' ? 'year(s)' : t('forms.Age') : ''}</span>
					</div>
					<div className="col-xs-4">
						<label className="param-label">
							{t('forms.Weight')}
							:
						</label>
						{item.weight || 'N/A'}
						<span className="mobile-hidden">&nbsp;</span>
						{item.weight ? 'lb' : ''}
					</div>
					<div className="col-xs-4">
						<label className="param-label">
							{t('forms.Height')}
							:
						</label>
						{item.height || 'N/A'}
						<span className="mobile-hidden">&nbsp;</span>
						{item.height ? 'ft' : ''}
					</div>
				</div>
				{item.caseType === CaseType.BreastAugmentation && <>
					<div className="row">
						{item.preBra && <div className="col-xs-6">
							<label className="param-label">
								{t('forms.Pre-op Size')}
								:
							</label>
							{item.preBra}
						</div>}
						{item.postBra && <div className="col-xs-6">
							<label className="param-label">
								{t('forms.Post-op Size')}
								:
							</label>
							{item.postBra}
						</div>}
					</div>
					<div className="row">
						{item.leftImplant && <div className="col-xs-6">
							<label className="param-label">
								{t('forms.Left Implant')}
								:
							</label>
							{item.leftImplant}
						</div>}
						{item.rightImplant && <div className="col-xs-6">
							<label className="param-label">
								{t('forms.Right Implant')}
								:
							</label>
							{item.rightImplant}
						</div>}
					</div>
				</>}
			</>}
		</div>
	</div>;
};

export default BeforeAfterBreast;
